import { Groupper, Icon, IconName, Label, Button, Message, useFrontier } from "react-frontier-v2";
import PriorityLabel from "./PriorityLabel";
import { IssuesStatus, type Issue } from "../SiteClasses";
import { useNavigate } from "react-router-dom";
import { useUser } from "../hooks";
import UserAccess from "../UserAccess";
import { ReactSortable } from "react-sortablejs";
import Sortable, { MultiDrag, Swap } from "sortablejs";
import { useEffect, useState } from "react";
import API from "../API";
import { getStatusName, getScheduleInfo, sortedToPriority } from "../SiteUtil";

interface Props {
   issues: Issue[];
}

export interface SortableIssue extends Issue {
   id: number;
}

Sortable.mount(new MultiDrag(), new Swap());

var ListBoardIssue = (props: Props) => {
   var navigate = useNavigate();
   var { hasAccess } = useUser();
   var { Toast } = useFrontier();

   var [columns, setColumns] = useState<Record<number, SortableIssue[]>>(() => {
      var cols: Record<number, SortableIssue[]> = {};
      Object.values(IssuesStatus).filter(v => typeof v !== 'string').forEach(v => {
         cols[v as number] = [];
      });
      (props.issues || []).forEach(issue => {
         var statusId = issue.status?.status_id;
         if (!statusId) return;
         if(statusId===IssuesStatus.CONFIRMED) statusId = IssuesStatus.RESOLVED;
         if (!cols[statusId]) cols[statusId] = [];
         cols[statusId].push({ ...issue, id: issue.issue_id });
      });
      return cols;
   });

   useEffect(() => {
      setColumns(() => {
         var cols: Record<number, SortableIssue[]> = {};
         Object.values(IssuesStatus).filter(v => typeof v !== 'string').forEach(v => {
            cols[v as number] = [];
         });
         (props.issues || []).forEach(issue => {
            var statusId = issue.status?.status_id;
            if (!statusId) return;
            if(statusId===IssuesStatus.CONFIRMED) statusId = IssuesStatus.RESOLVED;
            if (!cols[statusId]) cols[statusId] = [];
            cols[statusId].push({ ...issue, id: issue.issue_id });
         });
         return cols;
      });
   }, [props.issues]);

   var onIssueDrop = (issueId: number, newStatusId: number) => {
      API.updateIssueStatus(issueId, newStatusId).then((res)=>{
         if(res?.error) {
            Toast.error(res.message);
            return;
         }
         Toast.success('Tarea actualizada exitosamente.');
      }).catch(()=>{
         Toast.error('Error al actualizar la tarea.');
      });
   };

   return (
      <>
         {Object.entries(IssuesStatus)
            .filter(([_ , value]) => typeof value !== 'string' && value !== 5)
            .map(([name, value]) => (
               <div key={value} style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 20 }}>
                  <Groupper 
                     title={getStatusName(value as IssuesStatus, name)}
                     titleRight={hasAccess(UserAccess.ISSUES.CREATE_ISSUES) ? (
                        <Button onClick={() => navigate(`/issues/create?status_id=${value}`)} icon iconName="plus" />
                     ) : undefined} 
                     style={{ width: '100%', borderTop: '3px solid #313385' }} 
                     fitted 
                  />

                  {(columns[value as IssuesStatus] || []).length === 0 && (
                     <Message type="info" text="Sin tareas" centered/>
                  )}

                  <ReactSortable 
                     group={'issues'}
                     list={columns[value as IssuesStatus] || []} 
                     setList={(newList) => {
                        setColumns(prev => ({
                           ...prev,
                           [value]: newList
                        }));
                     }}
                     onAdd={(evt) => {
                        // Se dispara cuando un item es soltado en ESTA columna viniendo de otra
                        var issueId = evt.item.dataset.id;
                        if (issueId) {
                           onIssueDrop(Number(issueId), value as number);
                        }
                     }}
                     animation={200}
                     style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 10, flex: 1, minHeight: 50 }}
                  >
                     {(columns[value as IssuesStatus] || []).map((issue)=>{
                           var schedule = getScheduleInfo(issue.date_schedule, issue.status?.status_id);
                           return <div key={issue.id} data-id={issue.id} onClick={()=>navigate('/issues/'+issue.issue_id)} style={{ cursor: 'pointer' }}>              
                              <Groupper 
                                 title={`TASK-${issue.issue_id}`} 
                                 titleRight={<PriorityLabel priority={issue.priority} />}
                                 style={{ opacity: issue.status.status_id === 5 ? 0.6 : 1 }}
                              >
                                 <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    <div style={{ fontWeight: 700, fontSize: 16 }}>
                                       {issue.title.slice(0, 55) + (issue.title.length > 54 ? '...' : '')}   
                                    </div>
                                    <div 
                                       style={{
                                          display: 'flex', 
                                          gap: 5, 
                                          flexDirection: issue.issue_types.length>1?'column':'row', 
                                          alignItems: issue.issue_types.length>0?'flex-start':'normal' 
                                       }}
                                    >
                                       <Label value={issue.tag.tag_name} color="green" size="small"/>
                                       {issue.issue_types.length===1?(
                                          <Label 
                                             size="small"
                                             color="orange" 
                                             value={issue.issue_types[0].type_name.slice(0, 17) + (issue.issue_types[0].type_name.length > 16 ? '...' : '')}
                                             style={{fontSize: issue.issue_types[0].type_name.length>9 && 11 }}
                                             iconName={issue.issue_types[0].type_icon as IconName}
                                          />
                                       ):<div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                                          {sortedToPriority(issue.issue_types).map((type, index)=>(
                                             <Label 
                                                key={'type-'+index}
                                                size="small"
                                                color={index===0?"orange":'yellow'} 
                                                iconName={type.type_icon as IconName}
                                                value={index===0 && type.type_name}
                                             />
                                          ))}
                                       </div> 
                                       }
                                    </div>
                                    {schedule && (
                                       <div>
                                          <Label 
                                             size="small"
                                             color={schedule.isUrgent ? "red" : undefined}
                                             iconName="calendar-alt"
                                             value={schedule.text}
                                          />
                                       </div>
                                    )}
                                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                                       <div style={{ display: "flex" }}>
                                          {issue.users &&
                                             issue.users.map((user, index)=>(
                                                <div 
                                                   key={user.user_id} 
                                                   style={{ marginLeft: index === 0 ? 0 : -5 ,width: 20, height: 20, borderRadius: '50%', background: '#313385', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ffffff', fontSize: 10, border: '1px solid #fff' }}
                                                >
                                                   {(user.first_name?.[0] || '') + (user.last_name?.[0] || '')}
                                                </div>
                                             ))
                                          }
                                       </div>
                                       <div style={{ color: 'gray' }}>
                                          <Icon name="comment-alt" />
                                          {issue.comments_amount}
                                       </div>
                                    </div>
                                 </div>
                              </Groupper>
                           </div>;
                        })}
                  </ReactSortable>
               </div>
            ))
         }
      </>
   );
};

export default ListBoardIssue;